import axios from 'axios';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL;
const hasValidEnvBackendUrl =
  rawBackendUrl &&
  rawBackendUrl.trim() !== '' &&
  rawBackendUrl.trim().toLowerCase() !== 'undefined' &&
  rawBackendUrl.trim().toLowerCase() !== 'null';

const normalizeUrl = (url) => (url || '').trim().replace(/\/$/, '');

// eslint-disable-next-line no-control-regex
const MOJIBAKE_DETECTOR = /(?:\u00C3[\u0080-\u00BF]|\u00C2[\u0080-\u00BF]|\u00E2\u0080[\u0080-\u00BF]|\u0393)/;

const MOJIBAKE_REPLACEMENTS = [
  // UTF-8-as-Latin1 multi-byte sequences for arrow/dash characters
  ['\u00E2\u0086\u0092', '->'],        // â†' → ->
  ['\u00E2\u0086\u0090', '<-'],        // â†? → <-
  // Em dash and en dash (multi-byte mojibake)
  ['\u00E2\u0080\u0094', '\u2014'],    // â€" → em dash
  ['\u00E2\u0080\u0093', '\u2013'],    // â€" → en dash
  // Smart quotes → straight ASCII quotes
  ['\u201C', '"'],                      // \u201C left double quote → "
  ['\u201D', '"'],                      // \u201D right double quote → "
  ['\u2018', "'"],                      // \u2018 left single quote → '
  ['\u2019', "'"],                      // \u2019 right single quote → '
  // Dashes → hyphens
  ['\u2014', '-'],                      // em dash → -
  ['\u2013', '-'],                      // en dash → -
  // Ellipsis
  ['\u2026', '...'],                    // \u2026 ellipsis → ...
  // Whitespace normalization
  ['\u00A0', ' '],                      // non-breaking space → space
  ['\u00AD', ''],                       // soft hyphen → remove
  // Latin1-encoded accented characters (mojibake)
  ['\u00C3\u00A9', '\u00E9'],          // Ã© → é
  ['\u00C3\u00A8', '\u00E8'],          // Ã¨ → è
  ['\u00C3\u00AA', '\u00EA'],          // Ãª → ê
  ['\u00C3\u00A0', '\u00E0'],          // Ã  → à
  ['\u00C3\u00A7', '\u00E7'],          // Ã§ → ç
];

const decodeLatin1Mojibake = (text) => {
  try {
    const bytes = Uint8Array.from(Array.from(text).map((char) => char.charCodeAt(0) & 0xff));
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    return decoded || text;
  } catch {
    return text;
  }
};

const sanitizeMojibakeText = (text) => {
  if (typeof text !== 'string' || text.length === 0) return text;
  let output = text;

  if (MOJIBAKE_DETECTOR.test(output)) {
    output = decodeLatin1Mojibake(output);
  }

  MOJIBAKE_REPLACEMENTS.forEach(([bad, good]) => {
    output = output.split(bad).join(good);
  });
  // Safety net for residual mojibake clusters that start with Greek Gamma.
  output = output
    .replace(/\bΓ[^\s]{1,4}\b/g, '')
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')');
  return output.replace(/\s{2,}/g, ' ').trim();
};

const sanitizeMojibakeValue = (value) => {
  if (typeof value === 'string') return sanitizeMojibakeText(value);
  if (Array.isArray(value)) return value.map(sanitizeMojibakeValue);
  if (value && typeof value === 'object') {
    const entries = Object.entries(value).map(([key, nestedValue]) => [key, sanitizeMojibakeValue(nestedValue)]);
    return Object.fromEntries(entries);
  }
  return value;
};

const buildBackendCandidates = () => {
  const protocol = window.location.protocol;
  const hostOrigin = normalizeUrl(window.location.origin);
  const envUrl = hasValidEnvBackendUrl ? normalizeUrl(rawBackendUrl) : '';
  const isLocalRuntimeHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  const candidates = [envUrl, hostOrigin].filter(Boolean);

  // Keep localhost fallbacks for local development only.
  if (isLocalRuntimeHost) {
    candidates.push(`${protocol}//localhost:8001`, `${protocol}//127.0.0.1:8001`);
  }

  return [...new Set(candidates)].map((base) => `${base}/api`);
};

const API_BASE_CANDIDATES = buildBackendCandidates();
let activeApiBaseIndex = 0;

const getActiveApiBase = () => API_BASE_CANDIDATES[activeApiBaseIndex] || API_BASE_CANDIDATES[0];
const getPrimaryFrontendApiBase = () => `${normalizeUrl(window.location.origin)}/api`;
const trySwitchToNextApiBase = () => {
  if (activeApiBaseIndex >= API_BASE_CANDIDATES.length - 1) return false;
  activeApiBaseIndex += 1;
  api.defaults.baseURL = getActiveApiBase();
  return true;
};

/**
 * Get the appropriate CTA/affiliate URL for a tool with proper priority:
 * 1. User's personal Wowrolla affiliate referral link (highest commission potential)
 * 2. Tool's saved CTA URL (explicit signup/special offer URL)
 * 3. Official signup URL from tool data
 * 4. General affiliate/partner URL
 * 5. Website URL (fallback)
 * 
 * @param {Object} tool - The tool object with affiliate_link, cta_url, and website_url
 * @returns {string} The optimal URL for affiliate tracking and user experience
 */
const addWowrollaTracking = (url) => {
  if (!url) return url;
  try {
    const urlObj = new URL(url);
    // Add utm_source=wowrolla if not already present
    if (!urlObj.searchParams.has('utm_source')) {
      urlObj.searchParams.set('utm_source', 'wowrolla');
    }
    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original URL with appended param
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}utm_source=wowrolla`;
  }
};

export const getOptimalCTAUrl = (tool) => {
  if (!tool) return '';
  
    // Helper to apply utm parameters from tool config
    const applyUtmParams = (url) => {
      let finalUrl = addWowrollaTracking(url);
      if (tool.affiliate_link?.utm_parameters) {
        try {
          const urlObj = new URL(finalUrl);
          Object.entries(tool.affiliate_link.utm_parameters).forEach(([key, value]) => {
            if (value && !urlObj.searchParams.has(key)) {
              urlObj.searchParams.set(key, value);
            }
          });
          finalUrl = urlObj.toString();
        } catch {
          // Ignore URL parsing errors, return URL with just utm_source
        }
      }
      return finalUrl;
    };
  
  // Priority 1: Personal Wowrolla affiliate referral link
  if (tool.affiliate_link?.my_referral_link) {
    return applyUtmParams(tool.affiliate_link.my_referral_link);
  }
  
  // Priority 2: Tool's saved CTA/special offer URL (captured from source)
  if (tool.cta_url) {
    return applyUtmParams(tool.cta_url);
  }
  
  // Priority 3: Official signup URL
  if (tool.affiliate_link?.signup_url) {
    return applyUtmParams(tool.affiliate_link.signup_url);
  }
  
  // Priority 4: General affiliate/partner URL
  if (tool.affiliate_link?.url) {
    return applyUtmParams(tool.affiliate_link.url);
  }
  
  // Priority 5: Website URL (fallback)
  if (tool.website_url) {
    return applyUtmParams(tool.website_url);
  }
  
  return '';
};

export const api = axios.create({
  baseURL: getActiveApiBase(),
  timeout: 12000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    const expectedBinary = ['blob', 'arraybuffer'].includes(String(response?.config?.responseType || '').toLowerCase());
    const isBlobResponse = typeof Blob !== 'undefined' && response?.data instanceof Blob;
    const isArrayBufferResponse = response?.data instanceof ArrayBuffer;
    const isBinaryResponse = expectedBinary || isBlobResponse || isArrayBufferResponse;

    if (!isBinaryResponse) {
      response.data = sanitizeMojibakeValue(response.data);
    }

    // Detect HTML served by React dev server instead of real API response.
    // When REACT_APP_BACKEND_URL is not set the first candidate URL is the
    // React dev-server origin. That server returns index.html (200 OK) for
    // unknown paths. Axios resolves it as fulfilled, but the data is HTML, not
    // JSON. We reject it as a network-style failure so the retry logic can
    // switch to the next base URL (e.g. localhost:8001).
    if (!isBinaryResponse && typeof response.data === 'string' && response.data.trimStart().startsWith('<')) {
      const htmlErr = Object.assign(new Error('Received HTML instead of API JSON response'), {
        code: 'ERR_NETWORK',
        config: response.config,
        response: null,
      });
      return Promise.reject(htmlErr);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const activeBase = normalizeUrl(originalRequest.baseURL || api.defaults.baseURL || '');
    const isFrontendDevApiMiss =
      activeBase === getPrimaryFrontendApiBase() &&
      [404, 405].includes(error?.response?.status);
    const isNetworkFailure =
      !error.response ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNABORTED' ||
      isFrontendDevApiMiss;

    if (!isNetworkFailure || originalRequest.__baseRetryAttempted) {
      return Promise.reject(error);
    }

    let latestError = error;
    originalRequest.__baseRetryAttempted = true;

    while (trySwitchToNextApiBase()) {
      try {
        originalRequest.baseURL = getActiveApiBase();
        return await api.request(originalRequest);
      } catch (retryError) {
        latestError = retryError;
        const retryIsNetworkFailure = !retryError.response || retryError.code === 'ERR_NETWORK' || retryError.code === 'ECONNABORTED';
        if (!retryIsNetworkFailure) {
          break;
        }
      }
    }

    return Promise.reject(latestError);
  }
);

// Public API calls
export const publicApi = {
  getSiteSettings: () => api.get('/public/settings'),
  getPublicAuthConfig: () => api.get('/public/auth/config'),
  getCategories: () => api.get('/public/categories'),
  getCategoryBySlug: (slug) => api.get(`/public/categories/${slug}`),
  getToolsByCategory: (slug) => api.get(`/public/categories/${slug}/tools`),
  getToolBySlug: (slug) => api.get(`/public/tools/${slug}`),
  getTopToolsByClicks: (limit = 4) => api.get('/public/tools/top/by-clicks', { params: { limit } }),
  getAllTools: (featured = false) => api.get('/public/tools', { params: { featured } }),
  getBestPages: (limit = 200) => api.get('/public/best-pages', { params: { limit } }),
  getBestPageBySlug: (slug) => api.get(`/public/best-pages/${slug}`),
  getAlternativesPageSlugs: (limit = 5000) => api.get('/public/alternatives-pages/slugs', { params: { limit } }),
  getAlternativesPageBySlug: (slug) => api.get(`/public/alternatives-pages/${slug}`),
  getNewsletterIssues: (limit = 12) => api.get('/public/newsletter/issues', { params: { limit } }),
  trackToolClick: (toolId) => api.post(`/public/tools/${toolId}/track-click`),
  newsletterSubscribe: (payload) => api.post('/public/newsletter/subscribe', payload),
  newsletterConfirm: (token) => api.get('/public/newsletter/confirm', { params: { token } }),
  subscribe: (data) => api.post('/public/subscribe', data),
  search: (query) => api.get('/public/search', { params: { q: query } }),
  compareTools: (toolIds) => api.post('/public/compare', toolIds),
  getBlogPosts: () => api.get('/public/blog'),
  getBlogPost: (slug) => api.get(`/public/blog/${slug}`),
  getGuides: (params = {}) => api.get('/public/guides', { params }),
  getGuideBySlug: (slug) => api.get(`/public/guides/${slug}`),
  getSeoPages: (params = {}) => api.get('/public/seo-pages', { params }),
  getSeoPageBySlug: (slug) => api.get(`/public/seo-pages/${slug}`),
  getToolChangelog: (toolSlug) => api.get(`/public/tools/${toolSlug}/changelog`),
  getFitScores: (params = {}) => api.get('/public/fit-scores', { params }),
  getPartners: (params = {}) => api.get('/public/partners', { params }),
  getSponsorships: (params = {}) => api.get('/public/sponsorships', { params }),
  getFaqContent: () => api.get('/public/faq/content'),
  getToolReviews: (toolId) => api.get(`/public/tools/${toolId}/reviews`),
  getToolReviewsSummary: (toolId) => api.get(`/public/tools/${toolId}/reviews/summary`),
  createReview: (data) => api.post('/public/reviews', data),
  getFinderSession: (sessionId) => api.get(`/public/finder/session/${sessionId}`),
  updateFinderStack: (sessionId, toolId, action = 'add') =>
    api.post(`/public/finder/session/${sessionId}/stack`, { tool_id: toolId, action }),
  buildMyStack: (data) => api.post('/public/my-stack/build', data),
  saveMyStack: (data) => api.post('/public/my-stack/save', data),
  submitLead: (data) => api.post('/public/leads', data),
  getSharedStack: (slug) => api.get(`/public/stacks/${slug}`),
  getTransparencyTools: () => api.get('/public/transparency/tools'),
  findChat: (payload) => api.post('/find/chat', payload),
  saveFindSession: (payload) => api.post('/find/session', payload),
  getFindSessionById: (sessionId) => api.get(`/find/session/${sessionId}`),
  addFindToolToStack: (payload) => api.post('/find/add-to-stack', payload),
  finderChatStream: async (message, sessionId) => {
    const response = await fetch(`/api/public/finder/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, session_id: sessionId || null }),
    });

    if (!response.ok) {
      throw new Error('Finder chat request failed');
    }

    return response;
  },
};

// Admin API calls
export const authApi = {
  loginAdmin: (credentials) => api.post('/auth/login', credentials),
  registerAdmin: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  loginPublic: (credentials) => api.post('/auth/public/login', credentials),
  registerPublic: (payload) => api.post('/auth/public/register', payload),
  loginPublicWithGoogle: (payload) => api.post('/auth/public/google', payload),
  googleOAuthStart: () => `${getActiveApiBase()}/auth/google/nextauth/start`,
};

export const stackApi = {
  getDashboardStack: () => api.get('/public/dashboard/stack'),
  addStackItem: (payload) => api.post('/public/dashboard/stack/items', payload),
  updateStackItem: (toolId, payload) => api.put(`/public/dashboard/stack/items/${toolId}`, payload),
  removeStackItem: (toolId) => api.delete(`/public/dashboard/stack/items/${toolId}`),
  updateAlerts: (payload) => api.put('/public/dashboard/stack/alerts', payload),
  updatePublicSettings: (payload) => api.put('/public/dashboard/stack/public', payload),
  getCopyLinks: () => api.post('/public/dashboard/stack/copy-links'),
  runAlertsCheck: () => api.post('/public/dashboard/stack/alerts/check'),
};

const buildOrchestratorCandidates = () => {
  const envUrl = normalizeUrl(process.env.REACT_APP_ORCHESTRATOR_URL || '');
  const hostOrigin = normalizeUrl(window.location.origin);
  const isLocalDevHost =
    ['localhost', '127.0.0.1'].includes(window.location.hostname) &&
    ['3000', '5173'].includes(window.location.port);

  if (isLocalDevHost) {
    return ['/api/orchestrator'];
  }

  const candidates = [
    envUrl ? `${envUrl}/api/orchestrator` : '',
    hostOrigin ? `${hostOrigin}/api/orchestrator` : '',
  ].filter(Boolean);

  return [...new Set(candidates)];
};

const ORCHESTRATOR_BASES = buildOrchestratorCandidates();

export const orchestratorApi = {
  health: async () => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.get(`${base}/health`, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  createClient: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/clients`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  upsertMissionProfile: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/mission-profiles`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  createRun: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/runs`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  setApproval: async (runId, payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/runs/${runId}/approval`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  executeRun: async (runId, payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/runs/${runId}/execute`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  getLogs: async (tenantId, limit = 10) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.get(`${base}/execution-logs/${tenantId}`, {
          timeout: 12000,
          params: { limit },
        });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  getBusinessTypes: async () => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.get(`${base}/business-client-types`, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  getBusinessTypeDetail: async (businessType) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.get(`${base}/business-client-types/${businessType}`, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  bootstrapClient: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/clients/bootstrap`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  upsertUserStack: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/user-stacks`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  upsertJourneyStage: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/journey/stage`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  submitReadinessAudit: async (payload) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.post(`${base}/readiness-studio/submit`, payload, { timeout: 12000 });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
  getJourneyStages: async (tenantId, clientId) => {
    let lastError;
    for (const base of ORCHESTRATOR_BASES) {
      try {
        const response = await axios.get(`${base}/journey/stages`, {
          params: { tenant_id: tenantId, client_id: clientId },
          timeout: 10000,
        });
        return response.data;
      } catch (error) {
        lastError = error;
      }
    }
    throw lastError;
  },
};

const withAdminKeyHeader = (adminKey) => ({ headers: { 'X-Admin-Key': adminKey } });

export const consultingApi = {
  submitIntake: (payload) => api.post('/consulting/intake', payload),
  getClients: (adminKey, params = {}) => api.get('/consulting/clients', { ...withAdminKeyHeader(adminKey), params }),
  getClient: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}`, withAdminKeyHeader(adminKey)),
  updateStage: (adminKey, clientId, stage) => api.patch(`/consulting/clients/${clientId}/stage`, { stage }, withAdminKeyHeader(adminKey)),
  updateNotes: (adminKey, clientId, notes) => api.patch(`/consulting/clients/${clientId}/notes`, { notes }, withAdminKeyHeader(adminKey)),
  deleteClient: (adminKey, clientId) => api.delete(`/consulting/clients/${clientId}`, withAdminKeyHeader(adminKey)),
  generateDoc: (adminKey, clientId, docType) => api.post(`/consulting/clients/${clientId}/generate/${docType}`, {}, withAdminKeyHeader(adminKey)),
  getDocs: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/docs`, withAdminKeyHeader(adminKey)),
  syncClientToOrchestration: (adminKey, clientId, payload = {}) => api.post(`/consulting/clients/${clientId}/orchestration/sync`, payload, withAdminKeyHeader(adminKey)),
  addCustomWorkflow: (adminKey, clientId, payload) => api.post(`/consulting/clients/${clientId}/orchestration/custom-workflows`, payload, withAdminKeyHeader(adminKey)),
  listCustomWorkflows: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/orchestration/custom-workflows`, withAdminKeyHeader(adminKey)),
  generatePitchPackage: (adminKey, clientId, payload = {}) => api.post(`/consulting/clients/${clientId}/orchestration/pitch-package`, payload, withAdminKeyHeader(adminKey)),
  getPitchPackage: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/orchestration/pitch-package`, withAdminKeyHeader(adminKey)),
  downloadPitchPdf: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/orchestration/pitch-package/pdf`, { ...withAdminKeyHeader(adminKey), responseType: 'blob' }),
  downloadMyAiFlowPdf: (payload) => api.post('/consulting/my-ai-flow/pdf', payload, { responseType: 'blob' }),
  startDiscoveryCall: (adminKey, clientId, payload = {}) => api.post(`/consulting/clients/${clientId}/discovery/start`, payload, withAdminKeyHeader(adminKey)),
  getUserManagement: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/user-management`, withAdminKeyHeader(adminKey)),
  resetUserCredentials: (adminKey, clientId, payload) => api.post(`/consulting/clients/${clientId}/user-management/reset-credentials`, payload, withAdminKeyHeader(adminKey)),
  getClientHub360: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/hub-360`, withAdminKeyHeader(adminKey)),
  linkClientToMarketingHub: (adminKey, clientId, payload) => api.post(`/consulting/clients/${clientId}/marketing-hub/link`, payload, withAdminKeyHeader(adminKey)),
  recordToolPurchaseIntent: (payload) => api.post('/consulting/tool-purchase-intent', payload),
  // Readiness & session-stats persistence (authenticated user endpoints)
  saveReadiness: (payload) => api.post('/consulting/readiness', payload),
  getReadiness: () => api.get('/consulting/readiness'),
  getAiRuntimeLabel: () => api.get('/consulting/ai-runtime-label'),
  saveSessionStats: (payload) => api.post('/consulting/session-stats', payload),
  getClientReadinessStats: (adminKey, clientId) => api.get(`/consulting/clients/${clientId}/readiness-stats`, withAdminKeyHeader(adminKey)),
  getPublicCalendarSettings: () => api.get('/consulting/calendar/settings/public'),
  getCalendarSettings: (adminKey) => api.get('/consulting/calendar/settings', withAdminKeyHeader(adminKey)),
  updateCalendarSettings: (adminKey, payload) => api.put('/consulting/calendar/settings', payload, withAdminKeyHeader(adminKey)),
};

export const adminApi = {
  // Auth
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),

  // Dashboard
  getDashboardStats: () => api.get('/admin/dashboard/stats'),
  getSalesOverview: () => api.get('/admin/sales/overview'),
  getReadinessQuestions: (tenantId) => api.get('/admin/readiness/questions', { params: { tenant_id: tenantId } }),
  upsertReadinessQuestion: (payload) => api.post('/admin/readiness/questions', payload),
  getJourneyMasterView: (params = {}) => api.get('/admin/journey/master-view', { params }),
  getPilotShadowFeed: (params = {}) => api.get('/admin/pilot/shadow-feed', { params }),
  setPilotRunApproval: (runId, payload) => api.post(`/admin/pilot/runs/${runId}/approval`, payload),
  getAffiliateRevenueOverview: (params = {}) => api.get('/admin/affiliate/revenue-overview', { params }),

  // Categories
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  previewCategoryEnrichment: (data) => api.post('/admin/tool-enrichment/categories/preview', data),
  importCategoryFromSource: (data) => api.post('/admin/tool-enrichment/categories/import', data),

  // Tools
  getTools: (categoryId) => api.get('/admin/tools', { params: { category_id: categoryId } }),
  createTool: (data) => api.post('/admin/tools', data),
  updateTool: (id, data) => api.put(`/admin/tools/${id}`, data),
  deleteTool: (id) => api.delete(`/admin/tools/${id}`),
  exportToolJson: (id) => api.get(`/admin/tools/${id}/export`, { responseType: 'blob' }),
  importToolsJsonBulk: (formData) =>
    api.post('/admin/tools/import/json/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  previewToolEnrichment: (data) => api.post('/admin/tool-enrichment/tools/preview', data),
  importToolFromSource: (data) => api.post('/admin/tool-enrichment/tools/import', data),
  refreshToolFromSource: (id, data) => api.patch(`/admin/tool-enrichment/tools/${id}/refresh`, data),

  // Blog
  getBlogPosts: () => api.get('/admin/blog'),
  createBlogPost: (data) => api.post('/admin/blog', data),
  updateBlogPost: (id, data) => api.put(`/admin/blog/${id}`, data),
  deleteBlogPost: (id) => api.delete(`/admin/blog/${id}`),

  // Commissions
  getCommissions: (params = {}) => api.get('/admin/commissions', { params }),
  createCommission: (data) => api.post('/admin/commissions', data),
  updateCommission: (id, data) => api.put(`/admin/commissions/${id}`, data),
  deleteCommission: (id) => api.delete(`/admin/commissions/${id}`),
  updateCommissionStatus: (id, status) => api.put(`/admin/commissions/${id}/status`, null, { params: { status } }),
  getCommissionStats: (params = {}) => api.get('/admin/commissions/stats', { params }),

  // Subscribers
  getSubscribers: () => api.get('/admin/subscribers'),
  deleteSubscriber: (id) => api.delete(`/admin/subscribers/${id}`),

  // Content Generation
  generateContent: (data) => api.post('/admin/generate-content', data),

  // Repurpose Pipeline
  repurposeContent: (data) => api.post('/admin/repurpose', data),
  getRepurposeProgressUrl: (jobId) => `${api.defaults.baseURL}/admin/repurpose/${jobId}/progress`,
  getRepurposeJobStatus: (jobId) => api.get(`/admin/repurpose/${jobId}`),
  listRepurposeJobs: (limit = 20) => api.get('/admin/repurpose/history/jobs', { params: { limit } }),
  getRepurposeOutputs: (jobId, params = {}) => api.get(`/admin/repurpose/${jobId}/outputs`, { params }),
  publishRepurposeOutputs: (jobId) => api.post(`/admin/repurpose/${jobId}/publish-all`),
  scheduleRepurposeOutputs: (jobId, scheduledDate = null) =>
    api.post(`/admin/repurpose/${jobId}/schedule-all`, { scheduled_date: scheduledDate }),
  retryFailedRepurposeOutputs: (jobId) => api.post(`/admin/repurpose/${jobId}/retry-failed`),

  // Carousel media
  generateCarouselMedia: (data) => api.post('/admin/carousel/generate', data),
  getCarouselPreview: (contentId) => api.get(`/admin/carousel/${contentId}/preview`),

  // YouTube SEO/media
  optimizeYouTube: (data) => api.post('/admin/youtube/optimise', data),
  getYouTubeMetadata: (contentId) => api.get(`/admin/youtube/metadata/${contentId}`),

  // Publish workflow
  publishItem: (queueId) => api.post('/admin/publish', { queue_id: queueId }),
  publishBatch: (scheduledDate = null) => api.post('/admin/publish/batch', { scheduled_date: scheduledDate }),

  // Marketing Automation
  getQueue: () => api.get('/admin/queue'),
  markQueuePublished: (id, payload = { status: 'published' }) => api.patch(`/admin/queue/${id}`, payload),
  saveSettingsPost: (data) => api.post('/admin/settings', data),

  // Buffer Social Media
  getBufferProfiles: () => api.get('/admin/buffer/profiles'),
  createBufferPost: (text, profileIds, now = false) =>
    api.post('/admin/buffer/post', { text, profile_ids: profileIds, now }),

  // Webhooks
  triggerWebhook: (payload) => api.post('/admin/webhook/trigger', payload),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  getAiSettings: () => api.get('/admin/settings/ai'),
  updateAiSettings: (data) => api.put('/admin/settings/ai', data),

  // Finder
  getFinderAnalytics: () => api.get('/admin/finder/analytics'),
  updateFinderSettings: (data) => api.put('/admin/finder/settings', data),

  // Comparison Config
  getComparisonConfig: () => api.get('/admin/comparison/config'),
  updateComparisonConfig: (data) => api.put('/admin/comparison/config', data),
  getTopComparisonPairs: () => api.get('/admin/comparison/top-pairs'),

  // AEO Tracking
  getAeoScores: () => api.get('/admin/aeo/scores'),
  getAeoSettings: () => api.get('/admin/aeo/settings'),
  updateAeoSettings: (data) => api.put('/admin/aeo/settings', data),
  createAeoCitationLog: (data) => api.post('/admin/aeo/citation-log', data),
  getAeoCitationStats: (days = 7) => api.get('/admin/aeo/citation-stats', { params: { days } }),
  analyzeAeoPage: (data) => api.post('/admin/aeo/analyze-page', data),
  updateAeoPageNotes: (data) => api.put('/admin/aeo/page-notes', data),
  analyzeAeoLowScorePages: (data) => api.post('/admin/aeo/analyze-low-score-pages', data),
  getStaleSeoTools: (days = 90) => api.get('/admin/seo/stale-tools', { params: { days } }),

  // FAQ Admin
  getFaqEntries: () => api.get('/admin/faq'),
  generateFaqEntry: (data) => api.post('/admin/faq/generate', data),
  createFaqEntry: (data) => api.post('/admin/faq', data),
  updateFaqEntry: (id, data) => api.put(`/admin/faq/${id}`, data),
  deleteFaqEntry: (id) => api.delete(`/admin/faq/${id}`),

  // Reviews Admin
  getReviewsModeration: (status = 'pending') => api.get('/admin/reviews/moderation', { params: { status } }),
  moderateReview: (reviewId, action, note = '') => api.post(`/admin/reviews/${reviewId}/moderate`, { action, note }),
  importReviewsCsv: (csvContent, sourceLabel = 'Imported', defaultToolSlug = '', defaultToolId = '') => api.post('/admin/reviews/import-csv', { csv_content: csvContent, source_label: sourceLabel, default_tool_slug: defaultToolSlug || null, default_tool_id: defaultToolId || null }),
  importReviewsJson: (jsonContent, sourceLabel = 'Imported', defaultToolSlug = '', defaultToolId = '') => api.post('/admin/reviews/import-json', { json_content: jsonContent, source_label: sourceLabel, default_tool_slug: defaultToolSlug || null, default_tool_id: defaultToolId || null }),
  importReviewsFile: (file, { sourceLabel = 'Imported', sourceFormat = 'csv', defaultToolSlug = '', defaultToolId = '' } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source_label', sourceLabel);
    formData.append('source_format', sourceFormat);
    if (defaultToolSlug) formData.append('default_tool_slug', defaultToolSlug);
    if (defaultToolId) formData.append('default_tool_id', defaultToolId);
    return api.post('/admin/reviews/import-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getReviewSettings: () => api.get('/admin/reviews/settings'),
  updateReviewSettings: (data) => api.put('/admin/reviews/settings', data),
  getReviewSyncSources: () => api.get('/admin/reviews/sources'),
  saveReviewSyncSource: (payload) => api.post('/admin/reviews/sources', payload),
  runReviewSyncSource: (sourceId) => api.post(`/admin/reviews/sources/${sourceId}/sync`),
  deleteReviewSyncSource: (sourceId) => api.delete(`/admin/reviews/sources/${sourceId}`),
  summarizeToolReviews: (toolId) => api.post(`/admin/reviews/${toolId}/summarize`),

  // Growth Ops
  getOpsConfig: () => api.get('/admin/ops/config'),
  updateOpsConfig: (payload) => api.put('/admin/ops/config', payload),
  validateOpsConfig: () => api.get('/admin/ops/config/diagnostics'),
  getNewsletterDashboard: () => api.get('/admin/newsletter/dashboard'),
  updateNewsletterSettings: (payload) => api.put('/admin/newsletter/settings', payload),
  saveNewsletterCampaign: (payload) => api.post('/admin/newsletter/campaigns', payload),
  automateNewsletterCampaign: (campaignId, payload) => api.post(`/admin/newsletter/campaigns/${campaignId}/automation`, payload),
  deleteNewsletterCampaign: (campaignId) => api.delete(`/admin/newsletter/campaigns/${campaignId}`),
  getChangelogs: (toolId) => api.get('/admin/changelogs', { params: toolId ? { tool_id: toolId } : {} }),
  saveChangelog: (payload) => api.post('/admin/changelogs', payload),
  deleteChangelog: (changelogId) => api.delete(`/admin/changelogs/${changelogId}`),
  getSeoPages: () => api.get('/admin/seo-pages'),
  saveSeoPage: (payload) => api.post('/admin/seo-pages', payload),
  deleteSeoPage: (pageId) => api.delete(`/admin/seo-pages/${pageId}`),
  getFitScores: (params = {}) => api.get('/admin/fit-scores', { params }),
  updateFitScoreSettings: (payload) => api.put('/admin/fit-scores/settings', payload),
  getPricingMonitor: () => api.get('/admin/pricing-monitor'),
  capturePricingSnapshot: (payload) => api.post('/admin/pricing-monitor/capture', payload),
  getHealthMonitor: () => api.get('/admin/health-monitor'),
  getSystemAlerts: () => api.get('/admin/system-alerts'),
  getSystemEvents: (params = {}) => api.get('/admin/system-events', { params }),
  getSystemEventsAggregate: (params = {}) => api.get('/admin/system-events/aggregate', { params }),
  deleteSystemEventsGroup: (payload) => api.delete('/admin/system-events/group', { data: payload }),
  purgeSystemEvents: (payload) => api.delete('/admin/system-events', { data: payload }),
  getAuditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  getAuditLogsAggregate: (params = {}) => api.get('/admin/audit-logs/aggregate', { params }),
  deleteAuditLogsGroup: (payload) => api.delete('/admin/audit-logs/group', { data: payload }),
  purgeAuditLogs: (payload) => api.delete('/admin/audit-logs', { data: payload }),
  getSponsorships: () => api.get('/admin/sponsorships'),
  saveSponsorship: (payload) => api.post('/admin/sponsorships', payload),
  deleteSponsorship: (sponsorshipId) => api.delete(`/admin/sponsorships/${sponsorshipId}`),
  getSpotlight: () => api.get('/admin/spotlight'),
  updateSpotlight: (payload) => api.post('/admin/spotlight', payload),
  getGuidesAdmin: () => api.get('/admin/guides'),
  saveGuideAdmin: (payload) => api.post('/admin/guides', payload),
  deleteGuideAdmin: (guideId) => api.delete(`/admin/guides/${guideId}`),
  uploadGuideImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/guides/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPartnersAdmin: () => api.get('/admin/partners'),
  savePartnerAdmin: (payload) => api.post('/admin/partners', payload),
  deletePartnerAdmin: (partnerId) => api.delete(`/admin/partners/${partnerId}`),
  getSeoSettings: () => api.get('/admin/seo-settings'),
  updateSeoSettings: (payload) => api.put('/admin/seo-settings', payload),

  // User management (admin)
  listUsers: (params = {}) => api.get('/admin/users', { params }),
  createUser: (payload) => api.post('/admin/users', payload),
  updateUser: (userId, payload) => api.put(`/admin/users/${userId}`, payload),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  listUserTypes: () => api.get('/admin/user-types'),
  upsertUserType: (payload) => api.post('/admin/user-types', payload),
  disableUserType: (key) => api.delete(`/admin/user-types/${key}`),
};

export default api;
